import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Alert,
} from 'reactstrap';

import { AppState } from '../../../../store/store';
import ApiCaller from '../../../../lib/ApiCaller';
import SystemSettingsApi from '../../../../api/SystemSettingsApi';
import SystemSetting from '../../../../lib/common/models/systemSetting';
import { useSystemSettings } from '../../../../hooks';

const SystemSettings: React.FC = () => {
  const loggedUser = useSelector((state: AppState) => state.session.userInfo);
  const { ncapEnabled, updateNcapEnabled, refetchSettings } = useSystemSettings();
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [ncapToggle, setNcapToggle] = useState(ncapEnabled);

  useEffect(() => {
    loadSettings();
  }, [loggedUser.token]);

  useEffect(() => {
    setNcapToggle(ncapEnabled);
  }, [ncapEnabled]);

  const loadSettings = async () => {
    if (!loggedUser?.token) return;

    try {
      setLoading(true);
      const settingsApi = new SystemSettingsApi(new ApiCaller(loggedUser.token));
      const allSettings = await settingsApi.getAll();
      setSettings(allSettings);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Error loading system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleNcapToggle = async () => {
    const newValue = !ncapToggle;
    setLoading(true);

    try {
      const success = await updateNcapEnabled(newValue);
      if (success) {
        setNcapToggle(newValue);
        toast.success(
          `NCAP integration ${newValue ? 'enabled' : 'disabled'} successfully`,
        );
        loadSettings();
      } else {
        toast.error('Failed to update NCAP setting');
      }
    } catch (error) {
      console.error('Error updating NCAP setting:', error);
      toast.error('Failed to update NCAP setting');
    } finally {
      setLoading(false);
    }
  };

  const getNcapSetting = () => {
    return settings.find((s) => s.key === 'NCAP_INTEGRATION_ENABLED');
  };

  const ncapSetting = getNcapSetting();

  return (
    <div className="content">
      <Card>
        <CardHeader>
          <CardTitle tag="h4">System Settings</CardTitle>
        </CardHeader>
        <CardBody>
          <Alert color="info">
            <strong>Important:</strong> Changes to these settings take effect
            immediately and affect all users.
          </Alert>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle tag="h5">NCAP Integration (Navocerts)</CardTitle>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="8">
                  <p>
                    Controls the integration with NCAP (Navocerts) for
                    certificate operations. When disabled:
                  </p>
                  <ul>
                    <li>Certificate generation will be blocked</li>
                    <li>Certificate revocation will be blocked</li>
                    <li>Certificate reissue will be blocked</li>
                    <li>Certificate download will be blocked</li>
                    <li>
                      Automatic fishing certificate revocation will be skipped
                    </li>
                    <li>
                      Certificate-related buttons will be hidden from the UI
                    </li>
                  </ul>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <div className="d-flex align-items-center">
                      <Label className="mr-3 mb-0">
                        <strong>Status:</strong>
                      </Label>
                      <span
                        className={`badge badge-${ncapToggle ? 'success' : 'danger'} mr-3`}
                      >
                        {ncapToggle ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Button
                      color={ncapToggle ? 'danger' : 'success'}
                      onClick={handleNcapToggle}
                      disabled={loading}
                    >
                      {loading
                        ? 'Updating...'
                        : ncapToggle
                          ? 'Disable NCAP'
                          : 'Enable NCAP'}
                    </Button>
                  </FormGroup>
                </Col>
              </Row>

              {ncapSetting && (
                <Row className="mt-3">
                  <Col md="12">
                    <hr />
                    <small className="text-muted">
                      Last updated:{' '}
                      {ncapSetting.updatedAt
                        ? new Date(ncapSetting.updatedAt).toLocaleString()
                        : 'Never'}
                      {ncapSetting.updatedBy && (
                        <>
                          {' '}
                          by{' '}
                          <strong>
                            {ncapSetting.updatedBy.name ||
                              ncapSetting.updatedBy.email}
                          </strong>
                        </>
                      )}
                    </small>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>

          {!ncapToggle && (
            <Alert color="warning" className="mt-4">
              <strong>Warning:</strong> NCAP integration is currently disabled.
              Certificate operations are unavailable until it is re-enabled.
            </Alert>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default SystemSettings;

